<script setup lang="ts">
import { MContainerGrow, MContainerRow, useAssets } from "@xlabmedical/medic-web-framework";
import { useWindowSize } from "@vueuse/core";
import { computed } from "vue";

interface Props {
    projectName: string;
    hasWeblinkOrder: boolean;
    isLargeScreen: boolean;
    showSharesIcon: boolean;
    sharedWithText: string;
}

const props = defineProps<Props>();

const emits = defineEmits<{
    (e: "toggle-project-info"): void;
    (e: "open-weblink-order"): void;
}>();

const assets = useAssets();
const { width } = useWindowSize();

const isCompactHeader = computed(() => width.value < 1200);
const isPhoneView = computed(() => width.value <= 768);
const maxMobileProjectNameLength = 20;
const displayedProjectName = computed(() => {
    if (!isPhoneView.value || props.projectName.length <= maxMobileProjectNameLength) {
        return props.projectName;
    }

    return `${props.projectName.slice(0, maxMobileProjectNameLength - 3)}...`;
});

const projectIconSize = computed(() => {
    return {
        width: props.isLargeScreen ? "36px" : "20px",
        height: props.isLargeScreen ? "36px" : "20px",
    };
});
</script>

<template>
    <MContainerRow class="items-center px-4 pb-2 gap-2 rg-border-bottom" :class="{ 'pt-2': isCompactHeader }">
        <img :src="assets.getAssetUrl('/icons/btn_icon_info.svg')" class="cursor-pointer" v-tooltip="$t('web-rg.projectInfo')" alt="'img" @click="emits('toggle-project-info')" />
        <span
            :class="['pl-project-title', 'overflow-text', hasWeblinkOrder ? 'wb-project-card-project-name' : 'project-card-project-name']"
            :style="{ fontSize: isLargeScreen ? '' : '1.55rem', minWidth: '0', maxWidth: isPhoneView ? '20ch' : '100%' }"
            :title="projectName">
            {{ displayedProjectName }}
        </span>
        <MContainerGrow />

        <img
            v-if="hasWeblinkOrder"
            :style="projectIconSize"
            :src="assets.getAssetUrl('/icons/ik_shared_04_128.png')"
            v-tooltip.left="$t('shared.inProductionFilesAvailableOnCloud')"
            alt="'img"
            class="cursor-pointer"
            @click="emits('open-weblink-order')" />
        <img
            v-if="showSharesIcon"
            :style="projectIconSize"
            :src="assets.getAssetUrl('/icons/ik_shared_08_128.png')"
            v-tooltip.left="{ value: sharedWithText, class: 'rg-shared-with-tooltip-project' }"
            alt="'img" />
        <img :style="projectIconSize" :src="assets.getAssetUrl('/icons/ik_localCloud_02_128.png')" v-tooltip.left="$t('web-rg.cloudStudy')" alt="'img" class="mr-3" />
    </MContainerRow>
</template>

<style scoped></style>
